import {
  Component,
  OnInit,
  DestroyRef,
  ChangeDetectorRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexDataLabels,
  ApexPlotOptions,
  ApexGrid,
  ApexTooltip,
  ApexStroke,
  ApexFill,
  ApexLegend,
} from 'ng-apexcharts';
import { HttpParams } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";

// Services
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { ListSectionGroupMonthlySummariesService } from "../../../data/section-group/list-section-group-monthly-summaries.service";

import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { SECTION_GROUP_HOME_I18N } from "./section-group-home.i18n";

@Component({
  selector: "app-section-group-home",
  templateUrl: "./section-group-home.component.html",
  styleUrls: ["./section-group-home.component.scss"],
  imports: SHARED_IMPORTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionGroupAdminHomeComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutBreakpoint = inject(LayoutBreakpointService);
  private readonly cdr = inject(ChangeDetectorRef);

  // i18n
  protected readonly i18n = SECTION_GROUP_HOME_I18N;

  isLoading = signal(true);
  isHandset$: Observable<boolean>;
  readonly currentYear = new Date().getFullYear();
  readonly currentMonth = new Date().getMonth() + 1; // 1-based
  areaChartPeriodLabel = '';

  // Section group
  selectedSectionGroup: any;

  error: any;

  // Stat card values
  numOfSections = 0;
  numOfStaffs = 0;
  numOfContributors = 0;
  numOfUsers = 0;

  // Quota Info
  currentUsageGB = 0;
  quotaLimitGB = 100;
  remainingGB = 0;
  usagePercentage = 0;

  // Trends (month over month)
  sectionTrend = 0;
  staffTrend = 0;
  contributorTrend = 0;
  userTrend = 0;

  // Notices
  notices: { date: string; text: string; link?: string[] }[] = [
    { date: '2025/03/24', text: '「月次サマリー」から月間のデータ使用量をご確認いただけるようになりました。' },
    { date: '2025/02/07', text: '連絡帳をご利用の方向けに通知機能を追加しました。' },
    { date: '2025/02/07', text: 'ユーザーサマリーをご利用の方向けのPDF出力にGOKURIレコードを追加できるように修正しました。' },
    { date: '2024/09/25', text: 'ユーザーサマリーをご利用の方向けのPDF出力のデザインを修正しました。' },
    { date: '2024/09/09', text: 'GOST機能をアップデートしました。各レコード表示UIの修正をしました。' },
    { date: '2024/08/02', text: 'ユーザーリストのUIの修正を行い、最終更新日でソートできるように変更しました。' },
    { date: '2023/09/26', text: 'ユーザーサマリーをご利用の方向けに、日付選択やデータ修正機能を実装しました。' },
    { date: '2023/09/01', text: 'GOKURIクラウドサービスのバージョンアップを行い、UIを変更ました。' },
    { date: '2023/02/20', text: 'GOKURIクラウドサービスのバージョンアップを行いました。' },
    { date: '2022/10/08', text: 'GOKURIアプリのバージョンアップを行いました。' },
  ];

  // Theme-aware chart colors (resolved at init)
  private chartForeColor = '#757575';
  private chartGridColor = '#E0E0E0';

  // =========================================================================
  // Chart 1: Monthly Data Usage Bar Chart (top-level)
  // =========================================================================
  barChartSeries: ApexAxisChartSeries = [{ name: SECTION_GROUP_HOME_I18N.chart.yAxisDataUsage, data: [] }];
  barChartOptions!: ApexChart;
  barChartXAxis!: ApexXAxis;
  barChartYAxis!: ApexYAxis;
  barChartDataLabels: ApexDataLabels = { enabled: false };
  barChartPlotOptions: ApexPlotOptions = {
    bar: { borderRadius: 5, columnWidth: '55%' },
  };
  barChartStroke: ApexStroke = { show: true, width: 2, colors: ['transparent'] };
  barChartFill: ApexFill = { opacity: 0.88 };
  barChartGrid!: ApexGrid;
  barChartTooltip: ApexTooltip = {
    theme: 'dark',
    marker: { show: true },
    y: { formatter: (val: number) => `${val.toFixed(2)} GB` },
  };
  barChartColors = ['#6750A4'];

  // =========================================================================
  // Chart 2: Horizontal Bar - Section Data Usage Ranking (top 5)
  // =========================================================================
  private readonly RANK_CHART_MAX = 5;
  rankChartSeries: ApexAxisChartSeries = [{ name: SECTION_GROUP_HOME_I18N.chart.yAxisDataUsage, data: [] }];
  rankChartOptions!: ApexChart;
  rankChartPlotOptions: ApexPlotOptions = {
    bar: {
      horizontal: true,
      borderRadius: 4,
      barHeight: '60%',
      distributed: true,
    },
  };
  rankChartXAxis!: ApexXAxis;
  rankChartYAxis!: ApexYAxis;
  rankChartDataLabels: ApexDataLabels = {
    enabled: true,
    formatter: (val: number) => `${val.toFixed(2)} GB`,
    style: { fontSize: '11px', colors: ['#fff'] },
  };
  rankChartTooltip: ApexTooltip = {
    theme: 'dark',
    y: { formatter: (val: number) => `${val.toFixed(2)} GB` },
  };
  rankChartLegend: ApexLegend = { show: false };
  rankChartColors: string[] = [];

  // =========================================================================
  // Chart 3: Stacked Area - Section User Trend (past 6 months)
  // =========================================================================
  private readonly AREA_CHART_MONTHS = 6;
  areaChartSeries: ApexAxisChartSeries = [];
  areaChartOptions!: ApexChart;
  areaChartXAxis!: ApexXAxis;
  areaChartYAxis!: ApexYAxis;
  areaChartStroke: ApexStroke = { curve: 'smooth', width: 2 };
  areaChartFill: ApexFill = { opacity: 0.4 };
  areaChartDataLabels: ApexDataLabels = { enabled: false };
  areaChartGrid!: ApexGrid;
  areaChartLegend!: ApexLegend;
  areaChartTooltip: ApexTooltip = {
    theme: 'dark',
    marker: { show: true },
    x: { show: true },
  };

  // Color palette for sections
  private readonly sectionColors = [
    '#6750A4', '#42A5F5', '#66BB6A', '#FFA726', '#EF5350',
    '#AB47BC', '#26A69A', '#EC407A', '#7E57C2', '#5C6BC0',
  ];

  constructor(
    public common: DatabaseCacheService,
    private route: ActivatedRoute,
    private listSectionGroupMonthlySummariesService: ListSectionGroupMonthlySummariesService,
  ) {}

  ngOnInit() {
    this.isHandset$ = this.layoutBreakpoint.isHandset$;

    // Resolve theme-aware colors from CSS custom properties
    this.resolveThemeColors();
    this.initChartOptions();

    this.selectedSectionGroup = this.common.getSelectedSectionGroup();
    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.selectedSectionGroup = this.common.getSelectedSectionGroup();
      });

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const sectionGroupId = params.get("sectionGroupId");
        if (sectionGroupId) {
          this.loadMonthlySummaries(sectionGroupId);
        } else {
          this.isLoading.set(false);
        }
      });
  }

  private resolveThemeColors(): void {
    const style = getComputedStyle(document.documentElement);
    this.chartForeColor = style.getPropertyValue('--gokuri-text-secondary').trim() || '#757575';
    this.chartGridColor = style.getPropertyValue('--gokuri-border').trim() || '#E0E0E0';
  }

  private initChartOptions(): void {
    const fc = this.chartForeColor;
    const gc = this.chartGridColor;

    // Bar chart
    this.barChartOptions = {
      type: 'bar', height: 300, foreColor: fc,
      toolbar: { show: false }, animations: { enabled: true, speed: 500 },
    };
    this.barChartXAxis = {
      categories: [],
      title: { text: SECTION_GROUP_HOME_I18N.chart.xAxisMonth, style: { color: fc, fontSize: '12px' } },
      labels: { style: { colors: fc, fontSize: '11px' } },
      axisBorder: { show: false }, axisTicks: { show: false },
    };
    this.barChartYAxis = {
      min: 0,
      title: { text: SECTION_GROUP_HOME_I18N.chart.yAxisDataUsage, style: { color: fc, fontSize: '12px' } },
      labels: { style: { colors: fc, fontSize: '11px' }, formatter: (val: number) => val.toFixed(1) },
    };
    this.barChartGrid = { borderColor: gc, strokeDashArray: 1, xaxis: { lines: { show: false } } };

    // Rank chart
    this.rankChartOptions = {
      type: 'bar', height: 300, foreColor: fc,
      toolbar: { show: false }, animations: { enabled: true, speed: 500 },
    };
    this.rankChartXAxis = {
      categories: [],
      labels: { style: { colors: fc, fontSize: '11px' } },
    };
    this.rankChartYAxis = {
      labels: { style: { colors: fc, fontSize: '12px' }, maxWidth: 200 },
    };

    // Area chart
    this.areaChartOptions = {
      type: 'area', height: 300, foreColor: fc,
      stacked: true, toolbar: { show: false }, animations: { enabled: true, speed: 500 },
    };
    this.areaChartXAxis = {
      categories: [],
      title: { text: SECTION_GROUP_HOME_I18N.chart.xAxisMonth, style: { color: fc, fontSize: '12px' } },
      labels: { style: { colors: fc, fontSize: '11px' } },
      axisBorder: { show: false }, axisTicks: { show: false },
    };
    this.areaChartYAxis = {
      min: 0,
      title: { text: SECTION_GROUP_HOME_I18N.chart.yAxisUsers, style: { color: fc, fontSize: '12px' } },
      labels: { style: { colors: fc, fontSize: '11px' }, formatter: (val: number) => Math.round(val).toString() },
    };
    this.areaChartGrid = { borderColor: gc, strokeDashArray: 1, xaxis: { lines: { show: false } } };
    this.areaChartLegend = {
      show: true, position: 'top', horizontalAlign: 'left',
      fontSize: '12px', labels: { colors: fc },
    };
  }

  private loadMonthlySummaries(sectionGroupId: string): void {
    const year = this.currentYear;

    // Fetch from start of previous year if past-6-month window crosses year boundary
    // (bar chart needs full current year; area chart needs past 6 months)
    const needsPrevYear = this.currentMonth <= this.AREA_CHART_MONTHS;
    const fromYear = needsPrevYear ? year - 1 : year;
    const fromMonth = needsPrevYear ? 1 : 1;

    const params = new HttpParams()
      .set("fiscalYearFrom", fromYear)
      .set("fiscalMonthFrom", fromMonth)
      .set("fiscalYearTo", year)
      .set("fiscalMonthTo", 12)
      .set("sectionGroupIds", sectionGroupId);

    this.isLoading.set(true);
    this.listSectionGroupMonthlySummariesService
      .listSectionGroupMonthlySummaries(params)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe((res) => {
        const monthlySummaries = res as any[];

        // --- Bar chart: monthly total data usage (current year only) ---
        const barData: number[] = new Array(12).fill(0);
        monthlySummaries.forEach((summary) => {
          const sg = summary.sectionGroup;
          const month = sg?.fiscalMonth ?? 0;
          const yr = sg?.fiscalYear ?? this.currentYear;
          if (yr === this.currentYear && month >= 1 && month <= 12) {
            barData[month - 1] = (sg?.dataUsedTotalBytes ?? 0) / 1024 / 1024 / 1024;
          }
        });
        this.barChartXAxis = { ...this.barChartXAxis, categories: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] };
        this.barChartSeries = [{ name: SECTION_GROUP_HOME_I18N.chart.yAxisDataUsage, data: barData }];

        if (monthlySummaries.length > 0) {
          const latest = monthlySummaries[monthlySummaries.length - 1];
          const latestSections: any[] = latest.sections ?? [];
          const latestSG = latest.sectionGroup ?? {};

          // --- Stat cards ---
          this.numOfSections = latestSections.length;
          this.numOfStaffs = latestSections.reduce((sum: number, s: any) => sum + (s.numOfStaffs ?? 0), 0);
          this.numOfContributors = latestSections.reduce((sum: number, s: any) => sum + (s.numOfContributors ?? 0), 0);
          this.numOfUsers = latestSections.reduce((sum: number, s: any) => sum + (s.numOfUsers ?? 0), 0);

          this.currentUsageGB = (latestSG.dataUsedTotalBytes ?? 0) / 1024 / 1024 / 1024;
          this.quotaLimitGB = latestSG.monthlyQuotaGB ?? 100;
          this.remainingGB = Math.max(0, this.quotaLimitGB - this.currentUsageGB);
          this.usagePercentage = this.quotaLimitGB > 0
            ? Math.min(100, (this.currentUsageGB / this.quotaLimitGB) * 100)
            : 0;

          // Trends
          if (monthlySummaries.length > 1) {
            const prev = monthlySummaries[monthlySummaries.length - 2];
            const prevSections: any[] = prev.sections ?? [];
            this.sectionTrend = this.numOfSections - prevSections.length;
            this.staffTrend = this.numOfStaffs - prevSections.reduce((s: number, sec: any) => s + (sec.numOfStaffs ?? 0), 0);
            this.contributorTrend = this.numOfContributors - prevSections.reduce((s: number, sec: any) => s + (sec.numOfContributors ?? 0), 0);
            this.userTrend = this.numOfUsers - prevSections.reduce((s: number, sec: any) => s + (sec.numOfUsers ?? 0), 0);
          }

          // --- Rank chart: section data usage ranking (latest month, top N) ---
          const allRanked = latestSections
            .map((s: any, i: number) => ({
              name: s.sectionName ?? 'Unknown',
              gb: (s.dataUsedBytes ?? 0) / 1024 / 1024 / 1024,
              color: this.sectionColors[i % this.sectionColors.length],
            }))
            .sort((a, b) => b.gb - a.gb);

          this.rankChartXAxis = { ...this.rankChartXAxis, categories: allRanked.map(r => r.name) };
          this.rankChartSeries = [{ name: SECTION_GROUP_HOME_I18N.chart.yAxisDataUsage, data: allRanked.map(r => parseFloat(r.gb.toFixed(3))) }];
          this.rankChartColors = allRanked.map(r => r.color);
          this.rankChartOptions = { ...this.rankChartOptions, height: Math.max(200, allRanked.length * 50) };

          // --- Area chart: section user count trend (past 6 months) ---
          // Calculate the 6-month window ending at current month
          const endMonth = this.currentMonth;
          const monthWindow: { year: number; month: number; label: string }[] = [];
          for (let i = this.AREA_CHART_MONTHS - 1; i >= 0; i--) {
            let m = endMonth - i;
            let y = this.currentYear;
            if (m <= 0) { m += 12; y -= 1; }
            monthWindow.push({ year: y, month: m, label: `${m}月` });
          }
          this.areaChartPeriodLabel = `${monthWindow[0].label}〜${monthWindow[monthWindow.length - 1].label}`;
          this.areaChartXAxis = { ...this.areaChartXAxis, categories: monthWindow.map(w => w.label) };

          // Build per-section monthly user count data
          const sectionMap = new Map<string, { name: string; data: number[] }>();

          // Collect all sections from ALL months
          monthlySummaries.forEach((summary: any) => {
            const sections: any[] = summary.sections ?? [];
            sections.forEach((s: any) => {
              if (!sectionMap.has(s.sectionId)) {
                sectionMap.set(s.sectionId, { name: s.sectionName ?? 'Unknown', data: new Array(this.AREA_CHART_MONTHS).fill(0) });
              }
            });
          });

          // Populate only months within the window
          monthlySummaries.forEach((summary: any) => {
            const sg = summary.sectionGroup;
            const month = sg?.fiscalMonth ?? summary.sections?.[0]?.fiscalMonth ?? 0;
            const yr = sg?.fiscalYear ?? this.currentYear;
            if (month < 1 || month > 12) return;
            const idx = monthWindow.findIndex(w => w.year === yr && w.month === month);
            if (idx === -1) return;

            const sections: any[] = summary.sections ?? [];
            sections.forEach((s: any) => {
              const entry = sectionMap.get(s.sectionId);
              if (entry) {
                entry.data[idx] = (s.numOfUsers ?? 0);
              }
            });
          });

          this.areaChartSeries = Array.from(sectionMap.values()).map((entry, i) => ({
            name: entry.name,
            data: entry.data,
            color: this.sectionColors[i % this.sectionColors.length],
          }));
        }

        this.cdr.markForCheck();
      });
  }
}
